import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Sun, Smile, Glasses, Upload, X, Star, ArrowUpRight, Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIPS = [
  { icon: Sun, title: "Find Decent Light", desc: "Daylight near a window or outside in shade beats a flash every time. Avoid harsh midday sun straight on your face." },
  { icon: Glasses, title: "Show The Frames", desc: "One clear photo from the front and one from the side tells the whole story — that's where a frame's shape really shows." },
  { icon: Smile, title: "Be Yourself", desc: "Coffee in hand, at your desk, out with friends. Unposed, real photos are the ones other shoppers trust." },
  { icon: Camera, title: "Keep It Sharp", desc: "Hold steady, tap to focus on your face, and skip the heavy filters so the true colour of the frame comes through." },
];

export default function ShareYourLook() {
  const { user } = useAuth();
  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState("");
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);

  const load = () => {
    api.get("/reviews/eligible").then((res) => setEligible(res.data)).catch(() => {});
    api.get("/account/reviews").then((res) => setMine(res.data)).catch(() => {});
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const res = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, res.data.url]);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not upload that photo"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const item = eligible.find((x) => `${x.order_id}:${x.product_id}` === selected);
    if (!item) {
      toast.error("Choose which frames you're sharing");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", { order_id: item.order_id, product_id: item.product_id, rating, body, photos });
      toast.success("Thanks! We'll review your photos and send your 15% code once they're approved.");
      setSelected(""); setBody(""); resetForm();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not send that through"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => { setPhotos([]); setRating(5); load(); };

  return (
    <div data-testid="share-your-look-page">
      <section className="bg-charcoal py-16 sm:py-24">
        <div className="container-lookers max-w-3xl">
          <div className="text-teal-400 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-3">Real Customers. Real Photos.</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-5">
            Share Your Look, Get <span className="text-teal-400">15% Off</span>
          </h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">
            Send us a photo or two of you wearing your Lookers frames. Once we've approved them we'll email you a single-use code for 15% off your next pair — and your photos may appear on the frame you bought and across the site.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#share-form" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-white text-white hover:text-charcoal font-heading font-bold text-sm uppercase tracking-wide px-7 py-3.5 rounded-full transition-all hover:scale-[1.02] active:scale-95" data-testid="share-hero-cta">
              Send Your Photos <ArrowUpRight size={16} />
            </a>
            <Link to="/shop" className="inline-flex items-center gap-2 border border-white/25 text-white font-heading font-bold text-sm uppercase tracking-wide px-7 py-3.5 rounded-full hover:border-white transition-colors">
              Shop Frames
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="container-lookers">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 tracking-tight mb-2">What Makes A Great Photo</h2>
          <p className="text-sm text-neutral-500 mb-10 max-w-2xl">No studio, no styling. Just you in your frames — these few things make a photo genuinely useful to the next shopper.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-6 sm:divide-x sm:divide-neutral-200">
            {TIPS.map((t, i) => (
              <div key={t.title} className="sm:px-6 first:sm:pl-0" data-testid={`photo-tip-${i}`}>
                <t.icon size={26} className="text-teal-600 mb-3" strokeWidth={1.8} />
                <h3 className="font-heading font-extrabold text-base text-neutral-900 mb-1.5">{t.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-xs text-neutral-400 max-w-2xl">
            By sending photos you're giving Lookers permission to show them on this website and our social channels. We'll never sell them on, and you can ask us to take them down at any time.
          </div>
        </div>
      </section>

      <section id="share-form" className="py-16 sm:py-20 bg-[#F8F8F9]">
        <div className="container-lookers max-w-2xl">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 tracking-tight mb-6">Send Your Photos</h2>

          {!user ? (
            <div className="bg-white border border-neutral-200 rounded-2xl p-7 text-sm text-neutral-600" data-testid="share-login-prompt">
              You'll need to be logged in so we can match your photos to your order and send your code.
              <div className="mt-4 flex gap-3">
                <Link to="/login" className="bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wide px-5 py-3 rounded-full hover:bg-teal-600 transition-colors" data-testid="share-login-link">Log In</Link>
                <Link to="/register" className="border border-neutral-300 text-charcoal font-heading font-bold text-xs uppercase tracking-wide px-5 py-3 rounded-full hover:border-charcoal transition-colors">Create Account</Link>
              </div>
            </div>
          ) : eligible.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-2xl p-7 text-sm text-neutral-600" data-testid="share-no-orders">
              Once you've bought a pair from Lookers, this is where you'll send your photos and claim your 15% code.
              <div className="mt-4"><Link to="/shop" className="text-teal-700 font-heading font-bold text-xs uppercase tracking-wide underline">Browse Frames</Link></div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-2xl p-7 space-y-6" data-testid="share-form">
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-wide text-neutral-500 mb-2">Which frames?</label>
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  className="w-full bg-[#F8F8F9] border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 outline-none focus:border-teal-500"
                  data-testid="share-frame-select"
                >
                  <option value="">Choose a pair you've bought…</option>
                  {eligible.map((x) => (
                    <option key={`${x.order_id}:${x.product_id}`} value={`${x.order_id}:${x.product_id}`} disabled={x.already_submitted}>
                      {x.brand_name} {x.model} — order {x.order_number}{x.already_submitted ? " (already shared)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-wide text-neutral-500 mb-2">How are they?</label>
                <div className="flex gap-1.5" data-testid="share-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)} aria-label={`${n} star`} data-testid={`share-rating-${n}`}>
                      <Star size={26} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-neutral-300"} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-wide text-neutral-500 mb-2">A few words (optional)</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="How they fit, how you wear them, what you thought when they arrived…"
                  className="w-full bg-[#F8F8F9] border border-neutral-200 rounded-xl px-4 py-3 text-sm text-neutral-900 outline-none focus:border-teal-500"
                  data-testid="share-body-input"
                />
              </div>

              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-wide text-neutral-500 mb-2">Your photos (up to 5)</label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {photos.map((url) => (
                    <div key={url} className="relative w-20 h-20 rounded-xl overflow-hidden border border-neutral-200">
                      <img src={url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setPhotos((p) => p.filter((u) => u !== url))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" data-testid="share-remove-photo">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {photos.length < 5 && (
                    <label className="w-20 h-20 rounded-xl border-2 border-dashed border-neutral-300 flex items-center justify-center cursor-pointer hover:border-teal-500 transition-colors" data-testid="share-photo-dropzone">
                      {uploading ? <Loader2 size={18} className="animate-spin text-neutral-400" /> : <Upload size={18} className="text-neutral-400" />}
                      <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="share-photo-input" />
                    </label>
                  )}
                </div>
                <p className="text-xs text-neutral-400">At least one photo of you wearing the frames is needed for the 15% code.</p>
              </div>

              <button
                type="submit"
                disabled={submitting || uploading || !selected || photos.length === 0}
                className="w-full bg-charcoal text-white font-heading font-bold text-sm uppercase tracking-wide px-7 py-4 rounded-full hover:bg-teal-600 transition-colors disabled:opacity-40"
                data-testid="share-submit-button"
              >
                {submitting ? "Sending…" : "Send Photos & Claim 15%"}
              </button>
            </form>
          )}

          {mine.length > 0 && (
            <div className="mt-10" data-testid="share-my-submissions">
              <h3 className="font-heading font-extrabold text-lg text-neutral-900 mb-4">Your Submissions</h3>
              <div className="space-y-3">
                {mine.map((m) => (
                  <div key={m.id} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center justify-between gap-4" data-testid={`my-review-${m.id}`}>
                    <div>
                      <div className="font-heading font-bold text-sm text-neutral-900">{m.brand_name} {m.model}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{m.photos?.length || 0} photo{(m.photos?.length || 0) === 1 ? "" : "s"} · {m.rating}/5</div>
                      {m.reward_coupon_code && (
                        <div className="mt-2 text-xs font-mono font-bold text-teal-700" data-testid={`my-review-code-${m.id}`}>
                          Your code: {m.reward_coupon_code} — 15% off your next pair
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] font-heading font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shrink-0 ${m.status === "approved" ? "bg-teal-50 text-teal-700" : m.status === "rejected" ? "bg-neutral-100 text-neutral-500" : "bg-amber-50 text-amber-700"}`}>
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
